/**
 * Keeps the API awake while somebody is actually using it.
 *
 * The API is on a free tier that spins a service down after fifteen minutes
 * without inbound traffic, and the next request then pays a cold start of
 * roughly half a minute. Two different problems come out of that, and only one
 * of them is worth solving from a browser:
 *
 * **Mid-session sleep.** Once a room is joined, the connection carrying it is
 * a websocket. If long stretches pass with no HTTP request — everyone quiet,
 * nobody moving anything — the service can spin down underneath a session
 * that is still open. A cheap ping on a timer prevents that, and it costs
 * nothing when nobody is here, because nobody is here to run it.
 *
 * **Sleep when the site is empty.** A page cannot fix this: no visitors means
 * no page running means no pings, which is exactly the case that would need
 * covering. Only something outside the browser could, and that is a worse
 * trade than it looks — the free allowance is 750 instance hours a month
 * against roughly 730 hours in a month, so holding the service open around
 * the clock spends nearly all of it and risks every free service being
 * suspended for the rest of the month. Sleeping when idle is the design, not
 * a fault in it.
 *
 * So: awake while used, asleep when not.
 */

const API_URL = import.meta.env.VITE_API_URL ?? "";

/** Comfortably inside the fifteen minutes the platform allows. */
const INTERVAL_MS = 10 * 60 * 1000;

/**
 * Pokes the API without waiting for or caring about the answer.
 *
 * Failures are silence by design: this is opportunistic, and a keep-alive that
 * surfaces errors would report a cold start as a fault to someone who is
 * mid-conversation and cannot act on it anyway.
 */
export function pingApi(): void {
  fetch(`${API_URL}/api/health`, {
    method: "GET",
    cache: "no-store",
    // No cookie needed. The point is to arrive, not to be recognised.
    credentials: "omit",
  }).catch(() => {});
}

/**
 * Starts pinging until the returned function is called.
 *
 * Pauses while the tab is hidden, for two reasons: browsers throttle timers in
 * background tabs so heavily that the schedule cannot be relied on anyway, and
 * a tab left open for days in the background is not use — paying instance
 * hours for it is spending the month's allowance on nobody.
 */
export function startKeepAlive(): () => void {
  let timer: ReturnType<typeof setInterval> | undefined;

  const stop = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  const start = () => {
    if (timer !== undefined) return;
    timer = setInterval(pingApi, INTERVAL_MS);
  };

  const onVisibility = () => {
    if (document.hidden) {
      stop();
    } else {
      // Coming back is itself a moment worth a ping: the tab may have been
      // hidden for longer than the spin-down window.
      pingApi();
      start();
    }
  };

  if (!document.hidden) start();
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    stop();
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
